"use client";

import { Link } from "@/components/ui/Link";
import { Container } from "@/components/ui/Container";
import { MegaMenuIcon } from "@/components/layout/MegaMenuIcon";
import type { MegaPanel } from "@/lib/content/types";

type MegaMenuPanelProps = {
  panel?: MegaPanel;
  open: boolean;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

function FeaturedGraphic() {
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
    >
      <div className="isometric-grid absolute inset-0 opacity-60" />
      <div className="angular-accent absolute -right-8 top-0 h-full w-3/5 opacity-80" />
      <svg
        className="absolute -bottom-6 -right-4 h-40 w-40 text-white/[0.06]"
        viewBox="0 0 120 120"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M78 12 78 42 32 68 18 60 18 44 78 12Z"
        />
        <path
          fillRule="evenodd"
          d="M34 70 50 79 78 63 62 54 34 70Z"
        />
        <path
          fillRule="evenodd"
          d="M4 98 4 118 78 86 78 64 4 98Z"
        />
      </svg>
    </div>
  );
}

export function MegaMenuPanel({
  panel,
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
}: MegaMenuPanelProps) {
  if (!open || !panel) return null;

  // Dense multi-column layout for large sector lists (e.g. Industries).
  const dense = panel.links.length > 6;

  return (
    <div
      id="mega-menu-panel"
      role="region"
      aria-label={`${panel.eyebrow} menu`}
      className="absolute inset-x-0 top-full z-40 hidden lg:block"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="border-b border-border bg-background/80 backdrop-blur-md">
        <Container width="content" className={dense ? "py-8" : "py-10"}>
          {dense ? (
            <div className="grid gap-8 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-10 xl:grid-cols-[minmax(0,260px)_minmax(0,1fr)] xl:gap-12">
              <Link
                href={panel.featured.href}
                onClick={onClose}
                className="group relative flex min-h-[200px] flex-col justify-end overflow-hidden bg-white/[0.02] p-6 ring-1 ring-inset ring-border-strong transition-colors hover:bg-white/[0.04] hover:ring-white/25 xl:min-h-[240px] xl:p-7"
              >
                <FeaturedGraphic />
                <div className="relative">
                  <p className="text-xs uppercase tracking-[0.22em] text-muted">
                    {panel.eyebrow}
                  </p>
                  <h3 className="mt-3 max-w-[12rem] text-xl font-medium tracking-tight text-foreground transition-opacity group-hover:opacity-90 xl:text-2xl">
                    {panel.featured.title}
                  </h3>
                  <p className="mt-2 max-w-[14rem] text-sm leading-relaxed text-muted">
                    {panel.featured.description}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm text-foreground transition-all group-hover:gap-3">
                    {panel.featured.cta}
                    <span aria-hidden="true">→</span>
                  </span>
                </div>
              </Link>

              <div>
                <div className="mb-4 flex items-baseline justify-between gap-4 border-b border-white/[0.06] pb-3">
                  <p className="text-xs uppercase tracking-[0.22em] text-muted">
                    All {panel.links.length} sectors
                  </p>
                  <Link
                    href={panel.featured.href}
                    onClick={onClose}
                    className="text-xs text-muted transition-colors hover:text-foreground"
                  >
                    View page →
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 xl:grid-cols-3 2xl:grid-cols-4">
                  {panel.links.map((item, index) => (
                    <Link
                      key={`${item.href}-${item.label}`}
                      href={item.href}
                      onClick={onClose}
                      className="group rounded-sm px-2.5 py-2.5 transition-colors hover:bg-white/[0.04]"
                    >
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono text-[10px] text-muted/50 tabular-nums">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="text-sm font-medium text-foreground transition-opacity group-hover:opacity-90">
                          {item.label}
                        </span>
                      </span>
                      {item.description ? (
                        <span className="mt-0.5 block pl-6 text-xs leading-snug text-muted line-clamp-2">
                          {item.description}
                        </span>
                      ) : null}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.6fr)] lg:gap-14">
              <Link
                href={panel.featured.href}
                onClick={onClose}
                className="group relative flex min-h-[220px] flex-col justify-end overflow-hidden bg-white/[0.02] p-8 ring-1 ring-inset ring-border-strong transition-colors hover:bg-white/[0.04] hover:ring-white/25"
              >
                <FeaturedGraphic />
                <div className="relative">
                  <p className="text-xs uppercase tracking-[0.22em] text-muted">
                    {panel.eyebrow}
                  </p>
                  <h3 className="mt-4 max-w-xs text-2xl font-medium tracking-tight text-foreground transition-opacity group-hover:opacity-90">
                    {panel.featured.title}
                  </h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted">
                    {panel.featured.description}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm text-foreground transition-all group-hover:gap-3">
                    {panel.featured.cta}
                    <span aria-hidden="true">→</span>
                  </span>
                </div>
              </Link>

              <div className="grid gap-1 sm:grid-cols-2">
                {panel.links.map((item) => (
                  <Link
                    key={`${item.href}-${item.label}`}
                    href={item.href}
                    onClick={onClose}
                    className="group flex gap-4 rounded-sm px-3 py-4 transition-colors hover:bg-white/[0.03]"
                  >
                    {item.icon && (
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center text-muted ring-1 ring-inset ring-border-strong transition-colors group-hover:text-foreground group-hover:ring-white/25">
                        <MegaMenuIcon id={item.icon} />
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground transition-opacity group-hover:opacity-90">
                        {item.label}
                      </span>
                      <span className="mt-1 block text-sm leading-relaxed text-muted">
                        {item.description}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </Container>
      </div>
    </div>
  );
}
